import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../LanguageContext';
import { useAuth } from '../AuthContext';
import { useBrand } from '../BrandContext';
import { projLabel } from '../components/UnitsDeals/constants';
import {
  maintenanceIssues, maintenanceIssue, maintenanceStats, createMaintenanceIssue,
  updateMaintenanceIssue, maintenanceComment, maintenanceUploadMedia, maintenanceMediaBlobUrl,
  deleteMaintenanceIssue, maintenanceTeam, saveMaintenanceTeam,
  maintenanceAddSubtask, maintenanceUpdateSubtask, maintenanceDeleteSubtask,
  maintenanceWarrantyList, saveMaintenanceWarranty, deleteMaintenanceWarranty,
} from '../api';

// No hardcoded tenant projects — comes from the tenant's own config (brand.projects).
const DEFAULT_PROJECTS = [];
const STATUS = {
  open: ['جديد', 'New', '#6366f1'], assigned: ['مسند', 'Assigned', '#3b82f6'],
  in_progress: ['قيد التنفيذ', 'In progress', '#f59e0b'], closed: ['مغلق', 'Closed', '#16a34a'],
};
const PRIORITY = { urgent: ['عاجل', 'Urgent', '#dc2626'], high: ['عالٍ', 'High', '#f59e0b'], medium: ['متوسط', 'Medium', '#3b82f6'], low: ['منخفض', 'Low', '#64748b'] };
// Subtask lifecycle: member works it (pending→in_progress→done), supervisor validates (done→approved/rejected).
const SUBTASK_STATUS = {
  pending: ['بانتظار', 'To do', '#6366f1'], in_progress: ['قيد التنفيذ', 'Doing', '#f59e0b'],
  done: ['بانتظار الاعتماد', 'Awaiting review', '#3b82f6'], approved: ['معتمد', 'Approved', '#22c55e'],
  rejected: ['مُعاد للمعالجة', 'Sent back', '#ef4444'],
};
const CATEGORY = {
  electrical: ['كهرباء', 'Electrical'], plumbing: ['سباكة', 'Plumbing'], hvac: ['تكييف', 'HVAC'],
  elevator: ['مصاعد', 'Elevator'], structural: ['إنشائي', 'Structural'], finishing: ['تشطيبات', 'Finishing'],
  common: ['مناطق مشتركة', 'Common area'], safety: ['سلامة', 'Safety'], other: ['أخرى', 'Other'],
};
// All statuses, in workflow order. Any can be set directly in one tap (was a clunky
// step-by-step "next states" flow that made closing a request take several clicks).
const ALL_STATUSES = ['open', 'assigned', 'in_progress', 'closed'];
const quickBtn = (color, filled) => ({
  padding: '6px 12px', borderRadius: 8, border: `1.5px solid ${color}`, fontWeight: 800, fontSize: '0.76rem', cursor: 'pointer',
  background: filled ? color : color + '14', color: filled ? '#fff' : color, whiteSpace: 'nowrap',
});

export default function Maintenance() {
  const { lang, isRTL, t: tr } = useLanguage();
  const { user } = useAuth();
  const brand = useBrand();
  const ar = lang === 'ar';
  const t = (a, e) => (ar ? a : e);
  const isMgr = user?.role === 'admin' || user?.role === 'manager';
  const projects = (brand?.projects?.length ? brand.projects : DEFAULT_PROJECTS);
  const pLabel = (p) => projLabel(p, tr) || p;

  const [data, setData] = useState(null);
  const [stats, setStats] = useState({ byStatus: {}, open: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [fProject, setFProject] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fPriority, setFPriority] = useState('');
  const [showReport, setShowReport] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [showTeam, setShowTeam] = useState(false);
  const [showWarranty, setShowWarranty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [res, s] = await Promise.all([
      maintenanceIssues({ project: fProject, status: fStatus, priority: fPriority, limit: 100 }),
      maintenanceStats(fProject),
    ]);
    setData(res.error ? { issues: [] } : res);
    if (!s.error) setStats(s);
    setLoading(false);
  }, [fProject, fStatus, fPriority]);
  useEffect(() => { load(); }, [load]);

  // Change a status straight from the list card (no need to open the issue). stopPropagation
  // so the control doesn't also open the detail modal.
  const [savingId, setSavingId] = useState(null);
  const cardSetStatus = async (e, id, status) => {
    e.stopPropagation();
    setSavingId(id);
    const r = await updateMaintenanceIssue(id, { status });
    setSavingId(null);
    if (r && r.error) { window.alert((ar ? 'تعذّر التحديث: ' : 'Could not update: ') + r.error); return; }
    load();
  };

  const sLabel = (k) => (STATUS[k] ? t(STATUS[k][0], STATUS[k][1]) : k);
  const sColor = (k) => (STATUS[k] ? STATUS[k][2] : '#888');
  const pColor = (k) => (PRIORITY[k] ? PRIORITY[k][2] : '#888');
  const prLabel = (k) => (PRIORITY[k] ? t(PRIORITY[k][0], PRIORITY[k][1]) : k);
  const cLabel = (k) => (CATEGORY[k] ? t(CATEGORY[k][0], CATEGORY[k][1]) : k);
  const ago = (d) => { if (!d) return ''; const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000); return days <= 0 ? t('اليوم', 'today') : `${days}${t('ي', 'd')}`; };

  const issues = data?.issues || [];
  const sel = { padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.82rem', cursor: 'pointer' };
  const badge = (text, color) => <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: '0.66rem', fontWeight: 700, background: color + '20', color }}>{text}</span>;

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ maxWidth: 1040, margin: '0 auto', padding: '4px 4px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: '1.2rem' }}>🔧</span>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0 }}>{t('صيانة المباني', 'Building Maintenance')}</h2>
        <span style={{ marginInlineStart: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {isMgr && <button onClick={() => setShowTeam(true)} style={sel}>{t('فرق الصيانة', 'Teams')}</button>}
          {isMgr && <button onClick={() => setShowWarranty(true)} style={sel}>{t('تواريخ التسليم', 'Delivery dates')}</button>}
          <button onClick={() => setShowReport(true)} style={{ ...sel, background: 'var(--accent)', color: '#fff', border: 'none', fontWeight: 700 }}>+ {t('بلاغ صيانة', 'Report issue')}</button>
        </span>
      </div>

      {/* Stats strip */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {['open', 'assigned', 'in_progress', 'closed'].map(k => (
          <div key={k} onClick={() => setFStatus(fStatus === k ? '' : k)} style={{ flex: '1 1 90px', minWidth: 90, textAlign: 'center', padding: '8px 6px', borderRadius: 10, cursor: 'pointer', background: fStatus === k ? sColor(k) + '20' : 'var(--bg-secondary)', border: `1px solid ${fStatus === k ? sColor(k) : 'var(--border)'}` }}>
            <div style={{ fontSize: '1.3rem', fontWeight: 800, color: sColor(k) }}>{stats.byStatus[k] || 0}</div>
            <div style={{ fontSize: '0.64rem', color: 'var(--text-secondary)', marginTop: 2 }}>{sLabel(k)}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <select value={fProject} onChange={e => setFProject(e.target.value)} style={sel}>
          <option value="">{t('كل المشاريع', 'All projects')}</option>
          {projects.map(p => <option key={p} value={p}>{pLabel(p)}</option>)}
        </select>
        <select value={fPriority} onChange={e => setFPriority(e.target.value)} style={sel}>
          <option value="">{t('كل الأولويات', 'All priorities')}</option>
          {Object.keys(PRIORITY).map(k => <option key={k} value={k}>{prLabel(k)}</option>)}
        </select>
        {fStatus && <button onClick={() => setFStatus('')} style={sel}>✕ {sLabel(fStatus)}</button>}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>...</div>
      ) : issues.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>{t('لا توجد بلاغات', 'No issues')}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {issues.map(i => (
            <div key={i.id} onClick={() => setOpenId(i.id)} style={{ border: '1px solid var(--border)', borderInlineStart: `3px solid ${sColor(i.status)}`, borderRadius: 12, padding: '12px 14px', background: 'var(--bg-secondary)', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                <b style={{ fontSize: '0.95rem' }}>{i.title}</b>
                {badge(sLabel(i.status), sColor(i.status))}
                {badge(prLabel(i.priority), pColor(i.priority))}
                <span style={{ marginInlineStart: 'auto', fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{ago(i.created_at)}</span>
              </div>
              <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <span>🏢 {pLabel(i.project) || '—'}{i.location ? ` · ${i.location}` : ''}{i.unit_code ? ` · ${i.unit_code}` : ''}</span>
                <span>🏷️ {cLabel(i.category)}</span>
                {i.supervisor && <span>👷 {i.supervisor}</span>}
                {i.assignee && <span>🛠️ {i.assignee}</span>}
                {i.subtask_count > 0 && <span>🧩 {i.subtask_done}/{i.subtask_count}</span>}
                {i.media_count > 0 && <span>📎 {i.media_count}</span>}
              </div>
              {/* Quick actions right on the card — change status without opening the issue.
                  Kept simple on purpose: Start (begin work) and Close (done). The dropdown
                  still exposes the full set (resolved/on-hold/cancelled) for anyone who needs it. */}
              <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--border)' }}>
                {(i.status === 'open' || i.status === 'assigned') && (
                  <button disabled={savingId === i.id} onClick={e => cardSetStatus(e, i.id, 'in_progress')} style={quickBtn('#f59e0b')}>🔧 {t('ابدأ العمل', 'Start')}</button>
                )}
                {i.status !== 'closed' && (
                  <button disabled={savingId === i.id} onClick={e => cardSetStatus(e, i.id, 'closed')} style={quickBtn('#16a34a', true)}>✓ {t('إغلاق البلاغ', 'Close')}</button>
                )}
                {i.status === 'closed' && (
                  <button disabled={savingId === i.id} onClick={e => cardSetStatus(e, i.id, 'open')} style={quickBtn('#6b7280')}>↩ {t('إعادة فتح', 'Reopen')}</button>
                )}
                <select value={i.status} disabled={savingId === i.id} onClick={e => e.stopPropagation()} onChange={e => cardSetStatus(e, i.id, e.target.value)}
                  style={{ marginInlineStart: 'auto', fontSize: '0.74rem', padding: '5px 8px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer' }}>
                  {ALL_STATUSES.map(s => <option key={s} value={s}>{sLabel(s)}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {showReport && <ReportModal projects={projects} pLabel={pLabel} t={t} ar={ar} isRTL={isRTL} onClose={() => setShowReport(false)} onDone={() => { setShowReport(false); load(); }} />}
      {openId && <IssueDetail id={openId} isMgr={isMgr} me={user?.name} t={t} ar={ar} isRTL={isRTL}
        sLabel={sLabel} sColor={sColor} prLabel={prLabel} pColor={pColor} cLabel={cLabel} pLabel={pLabel}
        onClose={() => setOpenId(null)} onChanged={load} />}
      {showTeam && <TeamModal projects={projects} pLabel={pLabel} t={t} ar={ar} isRTL={isRTL} onClose={() => setShowTeam(false)} />}
      {showWarranty && <WarrantyModal projects={projects} pLabel={pLabel} t={t} ar={ar} isRTL={isRTL} onClose={() => setShowWarranty(false)} />}
    </div>
  );
}

// Admin/manager editor for unit delivery dates — drives the out-of-warranty flag so the
// dates can be maintained in-app instead of re-importing a sheet.
function WarrantyModal({ projects, pLabel, t, ar, isRTL, onClose }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ project: projects[0] || '', unit: '', delivery_date: '' });
  const [q, setQ] = useState('');
  const load = useCallback(async () => { const r = await maintenanceWarrantyList(); setRows(r.rows || []); }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!f.project || !f.unit.trim() || !/^\d{4}-\d{2}-\d{2}$/.test(f.delivery_date)) {
      window.alert(t('اختر المشروع والوحدة وتاريخ التسليم', 'Pick project, unit and a delivery date')); return;
    }
    setBusy(true);
    const r = await saveMaintenanceWarranty({ project: f.project, unit: f.unit.trim(), delivery_date: f.delivery_date });
    setBusy(false);
    if (r && r.error) { window.alert(t('تعذّر الحفظ: ', 'Could not save: ') + r.error); return; }
    setF({ project: f.project, unit: '', delivery_date: '' }); load();
  };
  const editDate = async (row, date) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    setBusy(true); await saveMaintenanceWarranty({ project: row.project, unit: row.unit, delivery_date: date }); setBusy(false); load();
  };
  const del = async (id) => {
    if (!window.confirm(t('حذف تاريخ تسليم هذه الوحدة؟', 'Delete this unit delivery date?'))) return;
    setBusy(true); await deleteMaintenanceWarranty(id); setBusy(false); load();
  };
  const inp = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.84rem' };
  const filtered = (rows || []).filter(r => !q.trim() || `${r.project} ${r.unit}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div dir={isRTL ? 'rtl' : 'ltr'} onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-primary)', borderRadius: 14, padding: 20, width: '100%', maxWidth: 640, maxHeight: '86vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontWeight: 800 }}>{t('تواريخ تسليم الوحدات (الضمان)', 'Unit delivery dates (warranty)')}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: 'var(--text-secondary)' }}>×</button>
        </div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 12 }}>
          {t('أي بلاغ صيانة بعد مرور سنة على تاريخ التسليم يُعلَّم «خارج الضمان».', 'A maintenance request raised over a year after delivery is flagged out-of-warranty.')}
        </div>
        {/* Add / update row */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end', marginBottom: 12, padding: 10, background: 'var(--bg-secondary)', borderRadius: 10 }}>
          <div style={{ flex: '1 1 150px' }}>
            <label style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{t('المشروع', 'Project')}</label>
            <select style={inp} value={f.project} onChange={e => setF(p => ({ ...p, project: e.target.value }))}>
              {projects.map(p => <option key={p} value={p}>{pLabel(p)}</option>)}
            </select>
          </div>
          <div style={{ flex: '1 1 110px' }}>
            <label style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{t('الوحدة', 'Unit')}</label>
            <input style={inp} value={f.unit} placeholder="A1" onChange={e => setF(p => ({ ...p, unit: e.target.value }))} />
          </div>
          <div style={{ flex: '1 1 140px' }}>
            <label style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{t('تاريخ التسليم', 'Delivery date')}</label>
            <input type="date" style={inp} value={f.delivery_date} onChange={e => setF(p => ({ ...p, delivery_date: e.target.value }))} />
          </div>
          <button onClick={add} disabled={busy} style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>{t('حفظ', 'Save')}</button>
        </div>
        <input style={{ ...inp, marginBottom: 10 }} placeholder={t('بحث بالمشروع أو الوحدة…', 'Search project or unit…')} value={q} onChange={e => setQ(e.target.value)} />
        {rows === null ? <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>…</div>
          : filtered.length === 0 ? <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{t('لا توجد تواريخ بعد — أضِف أعلاه.', 'No dates yet — add above.')}</div>
          : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead><tr style={{ color: 'var(--text-secondary)', textAlign: 'start' }}>
                <th style={{ padding: 6, textAlign: 'start' }}>{t('المشروع', 'Project')}</th>
                <th style={{ padding: 6, textAlign: 'start' }}>{t('الوحدة', 'Unit')}</th>
                <th style={{ padding: 6, textAlign: 'start' }}>{t('تاريخ التسليم', 'Delivery date')}</th>
                <th style={{ padding: 6 }}></th>
              </tr></thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: 6 }}>{pLabel(r.project) || r.project}</td>
                    <td style={{ padding: 6 }}>{r.unit}</td>
                    <td style={{ padding: 6 }}><input type="date" defaultValue={String(r.delivery_date).slice(0, 10)} onBlur={e => e.target.value && editDate(r, e.target.value)} style={{ ...inp, padding: '4px 6px', width: 150 }} /></td>
                    <td style={{ padding: 6, textAlign: 'end' }}><button onClick={() => del(r.id)} disabled={busy} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 700 }}>🗑</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modalBox = { background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 14, width: '100%', maxWidth: 620, maxHeight: '88vh', overflow: 'auto', padding: 18 };
const inp = { width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.86rem' };
const fld = (label, node) => <div style={{ marginBottom: 10 }}><label style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>{label}</label>{node}</div>;

function ReportModal({ projects, pLabel, t, ar, isRTL, onClose, onDone }) {
  const [f, setF] = useState({ project: projects[0] || '', unit_code: '', location: '', category: 'other', priority: 'medium', title: '', description: '', assignee: '' });
  const [team, setTeam] = useState([]);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const u = (k, v) => setF(p => ({ ...p, [k]: v }));
  useEffect(() => { if (f.project) maintenanceTeam(f.project).then(r => setTeam(r.members || [])); }, [f.project]);
  const save = async () => {
    if (!f.title.trim()) return;
    setSaving(true);
    const res = await createMaintenanceIssue(f);
    if (!res.error && res.id) {
      for (const file of files) await maintenanceUploadMedia(res.id, file, file.name);
      onDone();
    } else { setSaving(false); alert(res.error || t('تعذّر الحفظ', 'Could not save')); }
  };
  return (
    <div style={overlay} onClick={onClose}><div dir={isRTL ? 'rtl' : 'ltr'} style={modalBox} onClick={e => e.stopPropagation()}>
      <h3 style={{ margin: '0 0 14px', fontWeight: 800 }}>{t('بلاغ صيانة جديد', 'New maintenance issue')}</h3>
      {fld(t('العنوان', 'Title') + ' *', <input style={inp} value={f.title} onChange={e => u('title', e.target.value)} autoFocus />)}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 160px' }}>{fld(t('المشروع', 'Project'), <select style={inp} value={f.project} onChange={e => u('project', e.target.value)}>{projects.map(p => <option key={p} value={p}>{pLabel(p)}</option>)}</select>)}</div>
        <div style={{ flex: '1 1 160px' }}>{fld(t('رقم الوحدة', 'Unit'), <input style={inp} value={f.unit_code} onChange={e => u('unit_code', e.target.value)} />)}</div>
      </div>
      {fld(t('الموقع (المبنى / الدور / المكان)', 'Location (building / floor / area)'), <input style={inp} value={f.location} onChange={e => u('location', e.target.value)} />)}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 140px' }}>{fld(t('النوع', 'Category'), <select style={inp} value={f.category} onChange={e => u('category', e.target.value)}>{Object.keys(CATEGORY).map(k => <option key={k} value={k}>{t(CATEGORY[k][0], CATEGORY[k][1])}</option>)}</select>)}</div>
        <div style={{ flex: '1 1 140px' }}>{fld(t('الأولوية', 'Priority'), <select style={inp} value={f.priority} onChange={e => u('priority', e.target.value)}>{Object.keys(PRIORITY).map(k => <option key={k} value={k}>{t(PRIORITY[k][0], PRIORITY[k][1])}</option>)}</select>)}</div>
        <div style={{ flex: '1 1 140px' }}>{fld(t('إسناد إلى', 'Assign to'), <select style={inp} value={f.assignee} onChange={e => u('assignee', e.target.value)}><option value="">{t('لاحقًا', 'Later')}</option>{team.map(m => <option key={m} value={m}>{m}</option>)}</select>)}</div>
      </div>
      {fld(t('الوصف', 'Description'), <textarea rows={3} style={inp} value={f.description} onChange={e => u('description', e.target.value)} />)}
      {fld(t('صور / فيديو للمشكلة', 'Photos / video of the issue'), <input type="file" accept="image/*,video/*" multiple onChange={e => setFiles([...e.target.files])} style={{ fontSize: '0.8rem' }} />)}
      {files.length > 0 && <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: 8 }}>{files.length} {t('ملف', 'file(s)')}</div>}
      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button onClick={save} disabled={saving || !f.title.trim()} style={{ padding: '10px 22px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: (saving || !f.title.trim()) ? 0.6 : 1 }}>{saving ? '...' : t('إرسال البلاغ', 'Submit')}</button>
        <button onClick={onClose} style={{ padding: '10px 22px', borderRadius: 10, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer' }}>{t('إلغاء', 'Cancel')}</button>
      </div>
    </div></div>
  );
}

function IssueDetail({ id, isMgr, me, t, ar, isRTL, sLabel, sColor, prLabel, pColor, cLabel, pLabel, onClose, onChanged }) {
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const [team, setTeam] = useState([]);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [stForm, setStForm] = useState({ assignee: '', title: '' });
  const fileRef = React.useRef(null);
  const load = useCallback(async () => { const r = await maintenanceIssue(id); if (!r.error) setD(r); }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (d?.issue?.project) maintenanceTeam(d.issue.project).then(r => setTeam(r.members || [])); }, [d?.issue?.project]);

  const issue = d?.issue;
  // Anyone on the maintenance board (they hold page.maintenance to be here) can work issues —
  // not just managers/assignee — matching the server. Website issues arrive unassigned, so the
  // old gate hid the status buttons from the team entirely.
  const canEdit = isMgr || can('page.maintenance') || issue?.assignee === me || issue?.reporter === me;
  const doPatch = async (patch) => {
    // Offer (not force) a repair photo before closing: if there are no attachments yet,
    // suggest adding one; the user can still close without it.
    if (patch.status === 'closed' && (!d?.media || d.media.length === 0)) {
      const ok = window.confirm(t('سيُغلق البلاغ دون صورة من المعالجة. يُفضّل إرفاق صورة أولًا — هل تريد الإغلاق على أي حال؟', 'Closing without a repair photo. It is better to attach one first — close anyway?'));
      if (!ok) { pickMedia(); return; }
    }
    setBusy(true);
    const r = await updateMaintenanceIssue(id, patch);
    setBusy(false);
    if (r && r.error) { window.alert(t('تعذّر تحديث الحالة: ', 'Could not update: ') + r.error); return; }
    await load(); onChanged();
  };
  const addComment = async () => { if (!comment.trim()) return; setBusy(true); await maintenanceComment(id, comment.trim()); setComment(''); await load(); setBusy(false); };
  const pickMedia = () => fileRef.current?.click();
  const onFile = async (e) => { const files = [...e.target.files]; e.target.value = ''; if (!files.length) return; setBusy(true); for (const f of files) await maintenanceUploadMedia(id, f, f.name); await load(); onChanged(); setBusy(false); };
  const remove = async () => { if (!window.confirm(t('حذف هذا البلاغ نهائيًا؟', 'Delete this issue permanently?'))) return; setBusy(true); const r = await deleteMaintenanceIssue(id); if (!r.error) { onChanged(); onClose(); } else setBusy(false); };

  const subtasks = d?.subtasks || [];
  const iAmSupervisor = isMgr || issue?.supervisor === me;
  const addSubtask = async () => {
    if (!stForm.title.trim() || !stForm.assignee) return;
    setBusy(true);
    const r = await maintenanceAddSubtask(id, { assignee: stForm.assignee, title: stForm.title.trim() });
    setBusy(false);
    if (r && r.error) { window.alert(t('تعذّر إضافة المهمة: ', 'Could not add subtask: ') + r.error); return; }
    setStForm({ assignee: '', title: '' }); await load(); onChanged();
  };
  const patchSubtask = async (sid, patch) => {
    setBusy(true);
    const r = await maintenanceUpdateSubtask(sid, patch);
    setBusy(false);
    if (r && r.error) { window.alert(t('تعذّر التحديث: ', 'Could not update: ') + r.error); return; }
    await load(); onChanged();
  };
  const rejectSubtask = (sid) => {
    const note = window.prompt(t('سبب الإرجاع للمعالجة (اختياري):', 'Reason for sending back (optional):'));
    if (note === null) return; // cancelled
    patchSubtask(sid, { status: 'rejected', note });
  };
  const removeSubtask = async (sid) => {
    if (!window.confirm(t('حذف هذه المهمة الفرعية؟', 'Delete this subtask?'))) return;
    setBusy(true);
    const r = await maintenanceDeleteSubtask(sid);
    setBusy(false);
    if (r && r.error) { window.alert(t('تعذّر الحذف: ', 'Could not delete: ') + r.error); return; }
    await load(); onChanged();
  };

  if (!issue) return <div style={overlay} onClick={onClose}><div style={modalBox} onClick={e => e.stopPropagation()}>...</div></div>;
  const evLabel = (e) => {
    if (e.type === 'status') return `${e.from_status ? sLabel(e.from_status) + ' → ' : ''}${sLabel(e.to_status)}`;
    if (e.type === 'assign') return `${t('إسناد إلى', 'Assigned to')} ${e.note || '—'}`;
    if (e.type === 'priority') return `${t('الأولوية', 'Priority')}: ${prLabel(e.note)}`;
    if (e.type === 'media') return t('أُرفق ملف', 'Attached media') + (e.note ? ` (${e.note})` : '');
    if (e.type === 'supervisor') return `${t('المشرف', 'Supervisor')}: ${e.note || '—'}`;
    return e.note;
  };
  const evIcon = (e) => ({ status: '🔄', assign: '🛠️', priority: '⚑', media: '📎', comment: '💬', subtask: '🧩', supervisor: '👷' }[e.type] || '•');

  return (
    <div style={overlay} onClick={onClose}><div dir={isRTL ? 'rtl' : 'ltr'} style={{ ...modalBox, maxWidth: 680 }} onClick={e => e.stopPropagation()}>
      <input ref={fileRef} type="file" accept="image/*,video/*" multiple onChange={onFile} style={{ display: 'none' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 10, marginBottom: 8 }}>
        <div><h3 style={{ margin: 0, fontWeight: 800 }}>{issue.title}</h3>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginTop: 4 }}>🏢 {pLabel(issue.project) || '—'}{issue.location ? ` · ${issue.location}` : ''}{issue.unit_code ? ` · ${issue.unit_code}` : ''} · 🏷️ {cLabel(issue.category)}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: 'var(--text-secondary)', lineHeight: 1 }}>×</button>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <span style={{ padding: '3px 10px', borderRadius: 8, fontSize: '0.72rem', fontWeight: 700, background: sColor(issue.status) + '20', color: sColor(issue.status) }}>{sLabel(issue.status)}</span>
        <span style={{ padding: '3px 10px', borderRadius: 8, fontSize: '0.72rem', fontWeight: 700, background: pColor(issue.priority) + '20', color: pColor(issue.priority) }}>{prLabel(issue.priority)}</span>
        {issue.supervisor && <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', alignSelf: 'center' }}>👷 {t('المشرف', 'Supervisor')}: {issue.supervisor}</span>}
        {issue.assignee && <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', alignSelf: 'center' }}>🛠️ {issue.assignee}</span>}
        {issue.reporter && <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', alignSelf: 'center' }}>· {t('المُبلِّغ', 'Reporter')}: {issue.reporter}</span>}
      </div>
      {issue.warranty && issue.warranty.outOfWarranty && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 10, padding: '10px 12px', marginBottom: 12, fontSize: '0.84rem', fontWeight: 700, display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: '1.1rem' }}>⚠️</span>
          <span>طلب الصيانة خارج الضمان لمرور سنة على تسليم الوحدة{issue.warranty.deliveryDate ? ` (تاريخ التسليم: ${String(issue.warranty.deliveryDate).slice(0, 10)})` : ''}</span>
        </div>
      )}
      {issue.description && <div style={{ fontSize: '0.86rem', lineHeight: 1.6, marginBottom: 12, whiteSpace: 'pre-wrap' }}>{issue.description}</div>}

      {/* Media gallery */}
      {d.media.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {d.media.map(m => <MediaThumb key={m.id} m={m} t={t} />)}
        </div>
      )}

      {/* Status + actions — tap any status to set it directly (one click). */}
      {canEdit && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 6 }}>{t('الحالة — اضغط لتغييرها', 'Status — tap to change')}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {ALL_STATUSES.map(s => {
              const active = issue.status === s;
              return (
                <button key={s} disabled={busy || active} onClick={() => doPatch({ status: s })}
                  style={{ padding: '7px 14px', borderRadius: 20, border: `1.5px solid ${sColor(s)}`, cursor: active ? 'default' : 'pointer', fontWeight: 800, fontSize: '0.8rem',
                    background: active ? sColor(s) : sColor(s) + '12', color: active ? '#fff' : sColor(s), opacity: (busy && !active) ? 0.5 : 1 }}>
                  {active ? '● ' : ''}{sLabel(s)}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
            <select value={issue.assignee || ''} disabled={busy} onChange={e => doPatch({ assignee: e.target.value })} style={{ ...inp, width: 'auto', fontSize: '0.78rem', padding: '5px 8px' }}>
              <option value="">{t('بدون مسؤول', 'Unassigned')}</option>
              {team.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            {isMgr && (
              <select value={issue.supervisor || ''} disabled={busy} onChange={e => doPatch({ supervisor: e.target.value })} style={{ ...inp, width: 'auto', fontSize: '0.78rem', padding: '5px 8px' }} title={t('المشرف', 'Supervisor')}>
                <option value="">👷 {t('بدون مشرف', 'No supervisor')}</option>
                {team.map(m => <option key={m} value={m}>👷 {m}</option>)}
              </select>
            )}
            <select value={issue.priority} disabled={busy} onChange={e => doPatch({ priority: e.target.value })} style={{ ...inp, width: 'auto', fontSize: '0.78rem', padding: '5px 8px' }}>
              {Object.keys(PRIORITY).map(k => <option key={k} value={k}>{prLabel(k)}</option>)}
            </select>
            <button onClick={pickMedia} disabled={busy} style={{ ...inp, width: 'auto', fontSize: '0.78rem', padding: '5px 10px', cursor: 'pointer', color: 'var(--accent)' }}>📎 {t('إرفاق', 'Attach')}</button>
            {isMgr && <button onClick={remove} disabled={busy} style={{ ...inp, width: 'auto', fontSize: '0.78rem', padding: '5px 10px', cursor: 'pointer', color: 'var(--danger, #dc2626)', marginInlineStart: 'auto' }}>🗑️</button>}
          </div>
        </div>
      )}

      {/* Subtasks — split the work across members; the supervisor validates each */}
      {(subtasks.length > 0 || iAmSupervisor) && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>🧩 {t('المهام الفرعية', 'Subtasks')}</span>
            {subtasks.length > 0 && <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{subtasks.filter(s => s.status === 'approved').length}/{subtasks.length} {t('معتمدة', 'approved')}</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {subtasks.map(s => {
              const col = SUBTASK_STATUS[s.status] || ['', '', '#888'];
              const canWork = s.assignee === me || iAmSupervisor;
              return (
                <div key={s.id} style={{ border: '1px solid var(--border)', borderInlineStart: `3px solid ${col[2]}`, borderRadius: 10, padding: '9px 11px', background: 'var(--bg-secondary)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <b style={{ fontSize: '0.85rem', flex: '1 1 auto' }}>{s.title}</b>
                    <span style={{ padding: '2px 9px', borderRadius: 8, fontSize: '0.64rem', fontWeight: 700, background: col[2] + '20', color: col[2] }}>{t(col[0], col[1])}</span>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <span>🛠️ {s.assignee || '—'}</span>
                    {s.status === 'approved' && s.validated_by && <span style={{ color: '#16a34a' }}>✔ {t('اعتمدها', 'approved by')} {s.validated_by}</span>}
                  </div>
                  {s.status === 'rejected' && s.review_note && <div style={{ fontSize: '0.72rem', color: '#ef4444', marginTop: 4 }}>↩ {s.review_note}</div>}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' }}>
                    {canWork && s.status === 'pending' && <button disabled={busy} onClick={() => patchSubtask(s.id, { status: 'in_progress' })} style={quickBtn('#f59e0b')}>🔧 {t('ابدأ', 'Start')}</button>}
                    {canWork && s.status === 'in_progress' && <button disabled={busy} onClick={() => patchSubtask(s.id, { status: 'done' })} style={quickBtn('#3b82f6', true)}>✅ {t('تم الإنجاز', 'Mark done')}</button>}
                    {canWork && s.status === 'rejected' && <button disabled={busy} onClick={() => patchSubtask(s.id, { status: 'in_progress' })} style={quickBtn('#6b7280')}>↻ {t('استئناف', 'Resume')}</button>}
                    {iAmSupervisor && s.status === 'done' && (
                      <>
                        <button disabled={busy} onClick={() => patchSubtask(s.id, { status: 'approved' })} style={quickBtn('#16a34a', true)}>✔ {t('اعتماد', 'Approve')}</button>
                        <button disabled={busy} onClick={() => rejectSubtask(s.id)} style={quickBtn('#ef4444')}>↩ {t('إرجاع', 'Send back')}</button>
                      </>
                    )}
                    {iAmSupervisor && <button disabled={busy} onClick={() => removeSubtask(s.id)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>🗑️</button>}
                  </div>
                </div>
              );
            })}
          </div>
          {iAmSupervisor && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' }}>
              <select value={stForm.assignee} onChange={e => setStForm(p => ({ ...p, assignee: e.target.value }))} style={{ ...inp, width: 'auto', fontSize: '0.78rem', padding: '7px 8px' }}>
                <option value="">{t('العضو', 'Member')}</option>
                {team.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <input value={stForm.title} onChange={e => setStForm(p => ({ ...p, title: e.target.value }))} onKeyDown={e => e.key === 'Enter' && addSubtask()} placeholder={t('وصف المهمة لهذا العضو…', 'Subtask for this member…')} style={{ ...inp, flex: '1 1 180px' }} />
              <button onClick={addSubtask} disabled={busy || !stForm.title.trim() || !stForm.assignee} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: (busy || !stForm.title.trim() || !stForm.assignee) ? 0.5 : 1 }}>+ {t('إضافة', 'Add')}</button>
            </div>
          )}
          {iAmSupervisor && team.length === 0 && <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 6 }}>{t('أضف أعضاء فريق هذا المشروع أولًا من «فرق الصيانة».', 'Add this project’s team members first via “Teams”.')}</div>}
        </div>
      )}

      {/* Timeline */}
      <div style={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 6 }}>{t('المسار الزمني', 'Timeline')} ({d.timeline.length})</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        {d.timeline.map(e => (
          <div key={e.id} style={{ display: 'flex', gap: 8, fontSize: '0.8rem' }}>
            <span style={{ flexShrink: 0 }}>{evIcon(e)}</span>
            <div style={{ flex: 1 }}>
              <div style={{ lineHeight: 1.4 }}>{evLabel(e)}</div>
              <div style={{ fontSize: '0.66rem', color: 'var(--text-secondary)' }}>{e.actor || '—'} · {new Date(e.created_at).toLocaleString()}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Comment */}
      <div style={{ display: 'flex', gap: 8 }}>
        <input style={{ ...inp, flex: 1 }} placeholder={t('أضف ملاحظة…', 'Add a note…')} value={comment} onChange={e => setComment(e.target.value)} onKeyDown={e => e.key === 'Enter' && addComment()} />
        <button onClick={addComment} disabled={busy || !comment.trim()} style={{ padding: '0 16px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: (busy || !comment.trim()) ? 0.5 : 1 }}>{t('إضافة', 'Add')}</button>
      </div>
    </div></div>
  );
}

function MediaThumb({ m, t }) {
  const [url, setUrl] = useState(null);
  useEffect(() => { let alive = true; maintenanceMediaBlobUrl(m.id).then(u => { if (alive) setUrl(u); }).catch(() => {}); return () => { alive = false; if (url) URL.revokeObjectURL(url); }; /* eslint-disable-next-line */ }, [m.id]);
  const isVideo = (m.mime || '').startsWith('video');
  if (!url) return <div style={{ width: 96, height: 96, borderRadius: 8, background: 'var(--bg-secondary)', border: '1px solid var(--border)' }} />;
  return isVideo
    ? <video src={url} controls style={{ width: 140, height: 96, borderRadius: 8, background: '#000', objectFit: 'cover' }} />
    : <a href={url} target="_blank" rel="noopener noreferrer"><img src={url} alt={m.caption || ''} style={{ width: 96, height: 96, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--border)' }} /></a>;
}

function TeamModal({ projects, pLabel, t, ar, isRTL, onClose }) {
  const [project, setProject] = useState(projects[0] || '');
  const [allUsers, setAllUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => { axios.get('/api/auth/users').then(r => setAllUsers((r.data.users || []).filter(u => u.active !== 0 && u.role !== 'broker').map(u => u.name).filter(Boolean))).catch(() => setAllUsers([])); }, []);
  useEffect(() => { if (project) maintenanceTeam(project).then(r => setMembers(r.members || [])); }, [project]);
  const toggle = (name) => setMembers(m => m.includes(name) ? m.filter(x => x !== name) : [...m, name]);
  const save = async () => { setSaving(true); const r = await saveMaintenanceTeam(project, members); setSaving(false); setMsg(r.error ? t('خطأ', 'Error') : t('تم الحفظ', 'Saved')); setTimeout(() => setMsg(null), 3000); };
  return (
    <div style={overlay} onClick={onClose}><div dir={isRTL ? 'rtl' : 'ltr'} style={modalBox} onClick={e => e.stopPropagation()}>
      <h3 style={{ margin: '0 0 12px', fontWeight: 800 }}>{t('فرق الصيانة حسب المشروع', 'Maintenance teams by project')}</h3>
      {fld(t('المشروع', 'Project'), <select style={inp} value={project} onChange={e => setProject(e.target.value)}>{projects.map(p => <option key={p} value={p}>{pLabel(p)}</option>)}</select>)}
      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: 8 }}>{t('اختر أعضاء فريق صيانة هذا المشروع', 'Pick this project’s maintenance team members')}</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {allUsers.map(name => (
          <button key={name} onClick={() => toggle(name)} style={{ padding: '6px 12px', borderRadius: 20, border: `1px solid ${members.includes(name) ? 'var(--accent)' : 'var(--border)'}`, background: members.includes(name) ? 'var(--accent)' : 'transparent', color: members.includes(name) ? '#fff' : 'var(--text-primary)', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>{members.includes(name) ? '✓ ' : ''}{name}</button>
        ))}
        {allUsers.length === 0 && <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{t('لا يوجد موظفون', 'No employees')}</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={save} disabled={saving} style={{ padding: '10px 22px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>{saving ? '...' : t('حفظ الفريق', 'Save team')}</button>
        {msg && <span style={{ fontSize: '0.82rem', color: '#16a34a' }}>{msg}</span>}
      </div>
    </div></div>
  );
}
